"use client"

import * as React from "react"
import { useTranslations } from "@/components/I18nProvider"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@sarkiai/ui"
import { cn } from "@sarkiai/utils"

// FAQ category types
type FaqCategory = "vcard" | "pricing" | "sarkiai" | "enterprise" | "all"

const FAQ_TABS: FaqCategory[] = ['all', 'vcard', 'sarkiai', 'enterprise', 'pricing']

const FAQ_MAPPING: Record<Exclude<FaqCategory, 'all'>, number[]> = {
  vcard: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
  pricing: [14, 15, 16],
  sarkiai: [17, 20],
  enterprise: [18, 19, 21, 22],
}

interface FaqListProps {
  /** Single category to display (ignored if showTabs is true) */
  category?: FaqCategory
  /** Maximum number of items to show */
  limit?: number
  /** Additional CSS classes */
  className?: string
  /** Show the FAQ title */
  showTitle?: boolean
  /** Show category tabs (enables multi-category view) */
  showTabs?: boolean
  /** Search query to filter questions */
  searchQuery?: string
  /** Tab trigger size variant */
  tabSize?: "sm" | "md"
}

// Get questions by category with optional search filtering
const getQuestionsByCategory = (
  category: FaqCategory,
  searchQuery: string,
  t: (key: string) => string
): number[] => {
  let allNums: number[] = []

  if (category === "all") {
    allNums = Array.from({ length: 22 }, (_, i) => i + 1)
  } else {
    allNums = FAQ_MAPPING[category] || []
  }

  if (!searchQuery) return allNums

  return allNums.filter((num) => {
    const question = t(`question${num}`).toLowerCase()
    const response = t(`response${num}`).toLowerCase()
    const search = searchQuery.toLowerCase()
    return question.includes(search) || response.includes(search)
  })
}

// FAQ items accordion component
const FaqItems: React.FC<{
  questions: number[]
  t: (key: string) => string
  searchQuery?: string
}> = ({ questions, t, searchQuery = "" }) => {
  if (questions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Aucun résultat trouvé{searchQuery && ` pour "${searchQuery}"`}
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {questions.map((num) => {
        const key = `faq-item-${num}`
        return (
          <Accordion key={key} type="single" collapsible className="w-full">
            <AccordionItem
              value={key}
              className="border-black/5 dark:border-white/10 bg-white dark:bg-card/50 backdrop-blur-sm rounded-xl overflow-hidden px-4 border shadow-sm"
            >
              <AccordionTrigger className="text-left font-semibold hover:no-underline py-4 text-sm md:text-base">
                {t(`question${num}`)}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pt-2 pb-6 text-sm">
                {t(`response${num}`)}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )
      })}
    </div>
  )
}

const FaqList: React.FC<FaqListProps> = ({
  category = "all",
  limit,
  className,
  showTitle = true,
  showTabs = false,
  searchQuery = "",
  tabSize = "sm",
}) => {
  const t = useTranslations("faq")

  const tabClasses = tabSize === "md"
    ? "px-8 py-3 rounded-[1.25rem] uppercase tracking-widest text-[10px]"
    : "px-6 py-2.5 rounded-[1.25rem] uppercase tracking-widest text-[9px]"

  // Single category view (no tabs)
  if (!showTabs) {
    const questions = getQuestionsByCategory(category, searchQuery, t).slice(0, limit)

    return (
      <div className={cn("space-y-8", className)}>
        {showTitle && (
          <h2 className="text-3xl md:text-4xl uppercase tracking-tighter leading-none text-center text-foreground">
            {t('title')}
          </h2>
        )}
        <FaqItems questions={questions} t={t} searchQuery={searchQuery} />
      </div>
    )
  }

  // Tabbed multi-category view
  return (
    <div className={cn("space-y-8 px-4 lg:px-12", className)}>
      {showTitle && (
        <h2 className="text-3xl md:text-4xl uppercase tracking-tighter leading-none text-center text-foreground">
          {t('title')}
        </h2>
      )}

      <Tabs defaultValue="all" className="w-full">
        <div className="flex justify-center mb-8 overflow-x-auto pb-4 scrollbar-hide">
          <TabsList className="bg-black/5 dark:bg-white/5 p-1.5 rounded-[1.5rem] h-auto border border-black/5 dark:border-white/5">
            {FAQ_TABS.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className={cn(
                  tabClasses,
                  "data-[state=active]:bg-black dark:data-[state=active]:bg-white data-[state=active]:text-white dark:data-[state=active]:text-black transition-all"
                )}
              >
                {t(`tabs.${tab}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {FAQ_TABS.map((tab) => {
          const questions = getQuestionsByCategory(tab, searchQuery, t).slice(0, limit)
          return (
            <TabsContent key={tab} value={tab} className="outline-none">
              <FaqItems questions={questions} t={t} searchQuery={searchQuery} />
            </TabsContent>
          )
        })}
      </Tabs>
    </div>
  )
}

export default FaqList
